import React, { useState, useEffect, useCallback } from 'react';
import {
  getRegularEmployeesMaster,
  getRegularEmployeeSalaryData,
  saveRegularEmployeeSalaryData,
  confirmRegularEmployeeSalary,
  revertRegularEmployeeSalaryConfirmation,
  getConfirmedSalaries,
  getNonTaxableItems,
  addNonTaxableItem,
  deleteNonTaxableItem,
  BUSINESS_ENTITIES
} from '../firebase/firestore';

function RegularEmployeeBulkEntry({ selectedMonth, selectedYear, businessEntity, currentUser, onChangePeriod }) {
  const [masterEmployees, setMasterEmployees] = useState([]);
  const [salaryData, setSalaryData] = useState([]);
  const [confirmedSalaries, setConfirmedSalaries] = useState([]);
  const [editingCell, setEditingCell] = useState(null); // {employeeId, field}
  const [loading, setLoading] = useState(true);
  const [customDeductions, setCustomDeductions] = useState([]);
  const [showDeductionModal, setShowDeductionModal] = useState(false);
  const [newDeductionName, setNewDeductionName] = useState('');

  const updatePeriod = ({ year, month }) => {
    if (onChangePeriod) {
      onChangePeriod({ year, month });
    }
  };

  const handleYearSelect = (event) => {
    updatePeriod({ year: parseInt(event.target.value, 10), month: selectedMonth });
  };

  const handleMonthSelect = (event) => {
    updatePeriod({ year: selectedYear, month: parseInt(event.target.value, 10) });
  };

  const shiftMonth = (offset) => {
    const base = new Date(selectedYear, selectedMonth + offset, 1);
    updatePeriod({ year: base.getFullYear(), month: base.getMonth() });
  };

  const canChangePeriod = Boolean(onChangePeriod);
  const yearOptions = Array.from({ length: 7 }, (_, idx) => selectedYear - 3 + idx);
  const monthOptions = Array.from({ length: 12 }, (_, idx) => idx);

  const renderPeriodToolbar = () => (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => shiftMonth(-1)}
          disabled={!canChangePeriod}
          className={`px-3 py-2 rounded border border-gray-300 text-sm ${canChangePeriod ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'}`}
        >
          이전달
        </button>
        <select
          value={selectedYear}
          onChange={handleYearSelect}
          disabled={!canChangePeriod}
          className="px-3 py-2 border rounded"
        >
          {yearOptions.map((year) => (
            <option key={year} value={year}>
              {year}년
            </option>
          ))}
        </select>
        <select
          value={selectedMonth}
          onChange={handleMonthSelect}
          disabled={!canChangePeriod}
          className="px-3 py-2 border rounded"
        >
          {monthOptions.map((month) => (
            <option key={month} value={month}>
              {month + 1}월
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => shiftMonth(1)}
          disabled={!canChangePeriod}
          className={`px-3 py-2 rounded border border-gray-300 text-sm ${canChangePeriod ? 'hover:bg-gray-100' : 'opacity-50 cursor-not-allowed'}`}
        >
          다음달
        </button>
      </div>
      <div className="text-sm text-gray-500">
        조회 중: {selectedYear}년 {selectedMonth + 1}월
      </div>
    </div>
  );

  // 현재 사업자명 가져오기
  const getCurrentBusinessName = () => {
    const entity = BUSINESS_ENTITIES.find(e => e.id === businessEntity);
    return entity ? entity.name : '';
  };

  const loadNonTaxableItems = useCallback(async () => {
    if (!businessEntity) return;

    const result = await getNonTaxableItems(businessEntity);
    if (result.success) {
      setCustomDeductions(result.items);
    }
  }, [businessEntity]);

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      // 마스터 데이터 로드
      console.log('정규직원 마스터 데이터 로드 시작...');
      const masterResult = await getRegularEmployeesMaster();
      console.log('마스터 데이터 결과:', masterResult);

      if (masterResult.success) {
        // 현재 사업자에 해당하는 직원들만 필터링 (사업자 정보가 없는 직원은 첫 번째 사업자에 포함)
        const businessEmployees = masterResult.employees.filter(emp =>
          emp.businessEntity === businessEntity ||
          (!emp.businessEntity && businessEntity === BUSINESS_ENTITIES[0].id)
        );

        setMasterEmployees(businessEmployees);
        console.log('현재 사업자 직원 수:', businessEmployees.length);

        // 월별 급여 데이터 로드 (사업자별)
        const salaryResult = await getRegularEmployeeSalaryData(selectedYear, selectedMonth + 1, businessEntity);
        console.log('급여 데이터 결과:', salaryResult);

        if (salaryResult.success && salaryResult.salaryData.length > 0) {
          setSalaryData(salaryResult.salaryData);
        } else {
          // 급여 데이터가 없으면 현재 사업자의 마스터 데이터로 초기화
          const initialSalaryData = businessEmployees.map(emp => ({
            employeeId: emp.id,
            name: emp.name,
            position: emp.position,
            department: emp.department,
            baseSalary: emp.baseSalary || 0,
            overtimeHours: 0,
            holidayWorkDays: 0,
            mealAllowance: 100000,
            transportAllowance: 100000,
            customDeductions: {} // 비급여 공제 항목
          }));
          setSalaryData(initialSalaryData);
          console.log('초기 급여 데이터 생성:', initialSalaryData.length);
        }
      } else {
        console.error('마스터 데이터 로드 실패:', masterResult.error);
      }
    } catch (error) {
      console.error('데이터 로드 중 오류:', error);
    }

    // 확정된 급여 로드
    const confirmedResult = await getConfirmedSalaries(selectedYear, selectedMonth + 1);
    if (confirmedResult.success) {
      setConfirmedSalaries(confirmedResult.salaries);
    }

    setLoading(false);
  }, [businessEntity, selectedMonth, selectedYear]);

  useEffect(() => {
    loadData();
    loadNonTaxableItems();
  }, [loadData, loadNonTaxableItems]);

  // 비과세 항목 로드
  // 이전 달 데이터 복사
  const copyFromPreviousMonth = async () => {
    const prevMonth = selectedMonth === 0 ? 12 : selectedMonth;
    const prevYear = selectedMonth === 0 ? selectedYear - 1 : selectedYear;

    const result = await getRegularEmployeeSalaryData(prevYear, prevMonth);
    if (result.success && result.salaryData.length > 0) {
      setSalaryData(result.salaryData);
      alert(`${prevYear}년 ${prevMonth}월 데이터를 복사했습니다.`);
    } else {
      alert('이전 달 데이터가 없습니다.');
    }
  };

  // 급여 계산 함수
  const calculateSalary = (employee) => {
    const baseSalary = Number(employee.baseSalary) || 0;
    const overtimeHours = Number(employee.overtimeHours) || 0;
    const holidayWorkDays = Number(employee.holidayWorkDays) || 0;
    const mealAllowance = Number(employee.mealAllowance) || 0;
    const transportAllowance = Number(employee.transportAllowance) || 0;

    // 시급 계산 (월 209시간 기준)
    const hourlyRate = baseSalary / 209;
    const dailyRate = baseSalary / 30;

    // 야근수당 (시간 × 시급 × 1.5)
    const overtimePay = Math.floor(overtimeHours * hourlyRate * 1.5);

    // 휴일근무수당 (일수 × 일급 × 1.5)
    const holidayPay = Math.floor(holidayWorkDays * dailyRate * 1.5);

    // 총 급여
    const totalSalary = baseSalary + overtimePay + holidayPay + mealAllowance + transportAllowance;

    // 법정 공제 계산
    const nationalPension = Math.floor(totalSalary * 0.045);
    const healthInsurance = Math.floor(totalSalary * 0.03545);
    const longTermCare = Math.floor(healthInsurance * 0.1295);
    const employmentInsurance = Math.floor(totalSalary * 0.009);
    const incomeTax = Math.floor(totalSalary * 0.03);
    const localIncomeTax = Math.floor(incomeTax * 0.1);

    // 비급여 공제 계산
    let customDeductionTotal = 0;
    if (employee.customDeductions) {
      Object.values(employee.customDeductions).forEach(amount => {
        customDeductionTotal += Number(amount) || 0;
      });
    }

    const totalDeduction = nationalPension + healthInsurance + longTermCare +
                         employmentInsurance + incomeTax + localIncomeTax + customDeductionTotal;

    return {
      baseSalary,
      overtimePay,
      holidayPay,
      mealAllowance,
      transportAllowance,
      totalSalary,
      nationalPension,
      healthInsurance,
      longTermCare,
      employmentInsurance,
      incomeTax,
      localIncomeTax,
      customDeductionTotal,
      totalDeduction,
      netPay: totalSalary - totalDeduction
    };
  };

  // 셀 클릭 시 편집 모드 시작
  const handleCellClick = (employeeId, field) => {
    if (isConfirmed(employeeId)) {
      alert('확정된 급여는 수정할 수 없습니다. 확정을 취소한 후 다시 수정해주세요.');
      return;
    }
    setEditingCell({ employeeId, field });
  };

  // 셀 값 변경
  const handleCellChange = (index, field, value) => {
    const updatedData = [...salaryData];
    updatedData[index] = {
      ...updatedData[index],
      [field]: value
    };
    setSalaryData(updatedData);
  };

  // 편집 완료 (Enter 또는 포커스 벗어날 때)
  const handleCellBlur = () => {
    setEditingCell(null);
  };

  // Enter 키 처리
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      setEditingCell(null);
    }
  };

  // 비과세 항목 추가
  const addCustomDeduction = async () => {
    if (newDeductionName.trim() && businessEntity && currentUser) {
      const result = await addNonTaxableItem(
        businessEntity,
        { name: newDeductionName.trim() },
        currentUser.uid
      );

      if (result.success) {
        // 비과세 항목 다시 로드
        await loadNonTaxableItems();

        // 모든 직원 데이터에 새 공제 항목 추가
        const updatedData = salaryData.map(emp => ({
          ...emp,
          customDeductions: {
            ...emp.customDeductions,
            [result.id]: 0
          }
        }));
        setSalaryData(updatedData);

        setNewDeductionName('');
        setShowDeductionModal(false);
      } else {
        alert('비과세 항목 추가 실패: ' + result.error);
      }
    }
  };

  // 비과세 항목 삭제
  const removeCustomDeduction = async (deductionId) => {
    const result = await deleteNonTaxableItem(deductionId);

    if (result.success) {
      // 비과세 항목 다시 로드
      await loadNonTaxableItems();

      // 모든 직원 데이터에서 해당 공제 항목 제거
      const updatedData = salaryData.map(emp => {
        const newDeductions = { ...emp.customDeductions };
        delete newDeductions[deductionId];
        return {
          ...emp,
          customDeductions: newDeductions
        };
      });
      setSalaryData(updatedData);
    } else {
      alert('비과세 항목 삭제 실패: ' + result.error);
    }
  };

  // 비급여 공제 금액 변경
  const handleCustomDeductionChange = (employeeIndex, deductionId, value) => {
    const updatedData = [...salaryData];
    updatedData[employeeIndex] = {
      ...updatedData[employeeIndex],
      customDeductions: {
        ...updatedData[employeeIndex].customDeductions,
        [deductionId]: value
      }
    };
    setSalaryData(updatedData);
  };

  // 전체 저장
  const handleSaveAll = async () => {
    const result = await saveRegularEmployeeSalaryData(
      selectedYear,
      selectedMonth + 1,
      salaryData,
      businessEntity
    );

    if (result.success) {
      alert('급여 데이터가 저장되었습니다.');
    } else {
      alert('저장 실패: ' + result.error);
    }
  };

  // 급여 확정
  const handleConfirmSalary = async (employee) => {
    const calculated = calculateSalary(employee);
    const result = await confirmRegularEmployeeSalary(
      selectedYear,
      selectedMonth + 1,
      employee.employeeId,
      {
        name: employee.name,
        ...calculated,
        confirmedBy: 'admin'
      }
    );

    if (result.success) {
      alert(`${selectedYear}년 ${selectedMonth + 1}월 ${employee.name}님의 급여가 확정되었습니다.`);
      await loadData();
    } else {
      alert('급여 확정 실패: ' + result.error);
    }
  };

  const handleRevertSalary = async (employee) => {
    if (!window.confirm(`${selectedYear}년 ${selectedMonth + 1}월 ${employee.name}님의 급여 확정을 취소하시겠습니까?`)) {
      return;
    }

    const result = await revertRegularEmployeeSalaryConfirmation(
      selectedYear,
      selectedMonth + 1,
      employee.employeeId,
      currentUser?.name || currentUser?.email || 'admin'
    );

    if (result.success) {
      alert('급여 확정이 취소되었습니다. 이제 급여 정보를 수정할 수 있습니다.');
      await loadData();
    } else {
      alert('확정 취소 실패: ' + result.error);
    }
  };

  const findConfirmation = (employeeId) => {
    return confirmedSalaries.find(
      (salary) =>
        salary.employeeId === employeeId &&
        salary.employeeType === 'regular' &&
        (salary.status === 'confirmed' || !salary.status)
    );
  };

  const isConfirmed = (employeeId) => Boolean(findConfirmation(employeeId));

  // 붙여넣기 처리
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const rows = pastedData.split('\n').filter(row => row.trim() !== '');

    const updatedData = [...salaryData];

    rows.forEach((row) => {
      const columns = row.split('\t');
      if (columns.length >= 2) {
        const name = columns[0].trim();
        const employeeIndex = updatedData.findIndex(emp => emp.name === name);

        if (employeeIndex !== -1) {
          const target = updatedData[employeeIndex];
          if (isConfirmed(target.employeeId)) {
            return;
          }
          updatedData[employeeIndex] = {
            ...target,
            overtimeHours: Number(columns[1]) || 0,
            holidayWorkDays: Number(columns[2]) || 0,
            mealAllowance: Number(columns[3]) || target.mealAllowance,
            transportAllowance: Number(columns[4]) || target.transportAllowance
          };
        }
      }
    });

    setSalaryData(updatedData);
    alert('데이터가 붙여넣기되었습니다.');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        {renderPeriodToolbar()}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center py-8">
            <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600"></div>
            <p className="mt-2">정규직원 데이터를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  if (masterEmployees.length === 0) {
    return (
      <div className="container mx-auto p-4">
        {renderPeriodToolbar()}
        <div className="bg-yellow-100 border border-yellow-400 rounded p-4">
          <p className="text-center text-lg">
            등록된 정규직원이 없습니다.
          </p>
          <p className="text-center mt-2">
            먼저 <strong>정규직원 마스터</strong> 탭에서 직원을 등록해주세요.
          </p>
          <div className="text-center mt-4">
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              새로고침
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      {renderPeriodToolbar()}
      {/* 비과세 항목 관리 모달 */}
      {showDeductionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-h-[70vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">비과세 항목 관리</h3>

            <div className="mb-4">
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newDeductionName}
                  onChange={(e) => setNewDeductionName(e.target.value)}
                  placeholder="새 비과세 항목명"
                  className="flex-1 px-3 py-2 border rounded"
                  onKeyPress={(e) => e.key === 'Enter' && addCustomDeduction()}
                />
                <button
                  onClick={addCustomDeduction}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  추가
                </button>
              </div>
            </div>

            <div className="mb-4">
              <h4 className="font-semibold mb-2">현재 비과세 항목</h4>
              <div className="space-y-2">
                {customDeductions.filter(d => d.active).map(deduction => (
                  <div key={deduction.id} className="flex justify-between items-center p-2 bg-gray-100 rounded">
                    <span>{deduction.name}</span>
                    <button
                      onClick={() => removeCustomDeduction(deduction.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      삭제
                    </button>
                  </div>
                ))}
                {customDeductions.filter(d => d.active).length === 0 && (
                  <p className="text-gray-500 text-sm">추가된 비과세 항목이 없습니다.</p>
                )}
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowDeductionModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold">
              {selectedYear}년 {selectedMonth + 1}월 정규직원 급여 입력
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              사업자: {getCurrentBusinessName()}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowDeductionModal(true)}
              className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600"
            >
              비과세 관리
            </button>
            <button
              onClick={copyFromPreviousMonth}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              이전달 복사
            </button>
            <button
              onClick={handleSaveAll}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              전체 저장
            </button>
          </div>
        </div>

        <div className="bg-gray-100 p-2 rounded mb-4">
          <p className="text-sm">
            💡 엑셀에서 복사한 데이터를 테이블에 붙여넣기 가능합니다.
            (형식: 이름[탭]야근시간[탭]휴일근무일수[탭]식대[탭]교통비)
          </p>
        </div>

        <div className="bg-blue-50 p-3 rounded mb-4 border border-blue-200">
          <h4 className="font-bold text-sm mb-2">📊 수당 계산 방법</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <strong>야근수당:</strong> 시급 × 야근시간 × 1.5
              <br />
              <span className="text-gray-600">• 시급 = 기본급 ÷ 209시간 (월 기준근로시간)</span>
            </div>
            <div>
              <strong>휴일근무수당:</strong> 일급 × 휴일근무일수 × 1.5
              <br />
              <span className="text-gray-600">• 일급 = 기본급 ÷ 30일</span>
            </div>
          </div>
          <div className="mt-2 text-sm text-gray-600">
            <strong>4대보험 공제:</strong> 국민연금(4.5%), 건강보험(3.545%), 장기요양(건강보험의 12.95%), 고용보험(0.9%)
          </div>
        </div>

        <div className="overflow-x-auto" onPaste={handlePaste}>
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-4 py-2" rowSpan="2">이름</th>
                <th className="border px-4 py-2" rowSpan="2">부서/직급</th>
                <th className="border px-4 py-2" rowSpan="2">기본급</th>
                <th className="border px-4 py-2" colSpan="2">야근</th>
                <th className="border px-4 py-2" colSpan="2">휴일근무</th>
                <th className="border px-4 py-2" rowSpan="2">식대</th>
                <th className="border px-4 py-2" rowSpan="2">교통비</th>
                <th className="border px-4 py-2" rowSpan="2">총급여</th>
                {customDeductions.filter(d => d.active).map(deduction => (
                  <th key={deduction.id} className="border px-2 py-2 text-sm" rowSpan="2">
                    {deduction.name}
                  </th>
                ))}
                <th className="border px-4 py-2" rowSpan="2">공제계</th>
                <th className="border px-4 py-2" rowSpan="2">실수령액</th>
                <th className="border px-4 py-2" rowSpan="2">작업</th>
              </tr>
              <tr className="bg-gray-100">
                <th className="border px-2 py-1 text-sm">시간</th>
                <th className="border px-2 py-1 text-sm">수당</th>
                <th className="border px-2 py-1 text-sm">일수</th>
                <th className="border px-2 py-1 text-sm">수당</th>
              </tr>
            </thead>
            <tbody>
              {salaryData.map((employee, index) => {
                const calculated = calculateSalary(employee);
                const confirmed = isConfirmed(employee.employeeId);
                const isEditingCell = (field) =>
                  editingCell?.employeeId === employee.employeeId && editingCell?.field === field;

                return (
                  <tr key={employee.employeeId} className={confirmed ? 'bg-green-50' : ''}>
                    <td className="border px-4 py-2 font-bold">{employee.name}</td>
                    <td className="border px-4 py-2 text-sm">
                      {employee.department || '-'}/{employee.position || '-'}
                    </td>
                    <td
                      className="border px-4 py-2 text-right cursor-pointer hover:bg-gray-100"
                      onClick={() => handleCellClick(employee.employeeId, 'baseSalary')}
                    >
                      {isEditingCell('baseSalary') ? (
                        <input
                          type="number"
                          value={employee.baseSalary}
                          onChange={(e) => handleCellChange(index, 'baseSalary', e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyPress={handleKeyPress}
                          className="w-full px-2 py-1 border rounded"
                          autoFocus
                        />
                      ) : (
                        `₩${formatCurrency(employee.baseSalary)}`
                      )}
                    </td>
                    <td
                      className="border px-2 py-2 text-center cursor-pointer hover:bg-gray-100"
                      onClick={() => handleCellClick(employee.employeeId, 'overtimeHours')}
                    >
                      {isEditingCell('overtimeHours') ? (
                        <input
                          type="number"
                          value={employee.overtimeHours}
                          onChange={(e) => handleCellChange(index, 'overtimeHours', e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyPress={handleKeyPress}
                          className="w-full px-1 py-1 border rounded text-center"
                          autoFocus
                        />
                      ) : (
                        employee.overtimeHours || 0
                      )}
                    </td>
                    <td className="border px-2 py-2 text-right text-blue-600">
                      <div className="text-xs">
                        ₩{formatCurrency(calculated.overtimePay)}
                      </div>
                      {calculated.overtimePay > 0 && (
                        <div className="text-xs text-gray-500">
                          (시급×1.5)
                        </div>
                      )}
                    </td>
                    <td
                      className="border px-2 py-2 text-center cursor-pointer hover:bg-gray-100"
                      onClick={() => handleCellClick(employee.employeeId, 'holidayWorkDays')}
                    >
                      {isEditingCell('holidayWorkDays') ? (
                        <input
                          type="number"
                          value={employee.holidayWorkDays}
                          onChange={(e) => handleCellChange(index, 'holidayWorkDays', e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyPress={handleKeyPress}
                          className="w-full px-1 py-1 border rounded text-center"
                          autoFocus
                        />
                      ) : (
                        employee.holidayWorkDays || 0
                      )}
                    </td>
                    <td className="border px-2 py-2 text-right text-blue-600">
                      <div className="text-xs">
                        ₩{formatCurrency(calculated.holidayPay)}
                      </div>
                      {calculated.holidayPay > 0 && (
                        <div className="text-xs text-gray-500">
                          (일급×1.5)
                        </div>
                      )}
                    </td>
                    <td
                      className="border px-4 py-2 text-right cursor-pointer hover:bg-gray-100"
                      onClick={() => handleCellClick(employee.employeeId, 'mealAllowance')}
                    >
                      {isEditingCell('mealAllowance') ? (
                        <input
                          type="number"
                          value={employee.mealAllowance}
                          onChange={(e) => handleCellChange(index, 'mealAllowance', e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyPress={handleKeyPress}
                          className="w-full px-2 py-1 border rounded"
                          autoFocus
                        />
                      ) : (
                        `₩${formatCurrency(employee.mealAllowance)}`
                      )}
                    </td>
                    <td
                      className="border px-4 py-2 text-right cursor-pointer hover:bg-gray-100"
                      onClick={() => handleCellClick(employee.employeeId, 'transportAllowance')}
                    >
                      {isEditingCell('transportAllowance') ? (
                        <input
                          type="number"
                          value={employee.transportAllowance}
                          onChange={(e) => handleCellChange(index, 'transportAllowance', e.target.value)}
                          onBlur={handleCellBlur}
                          onKeyPress={handleKeyPress}
                          className="w-full px-2 py-1 border rounded"
                          autoFocus
                        />
                      ) : (
                        `₩${formatCurrency(employee.transportAllowance)}`
                      )}
                    </td>
                    <td className="border px-4 py-2 text-right font-bold">
                      ₩{formatCurrency(calculated.totalSalary)}
                    </td>
                    {customDeductions.filter(d => d.active).map(deduction => (
                      <td
                        key={deduction.id}
                        className="border px-2 py-2 text-right cursor-pointer hover:bg-gray-100"
                        onClick={() => handleCellClick(employee.employeeId, `custom_${deduction.id}`)}
                      >
                        {isEditingCell(`custom_${deduction.id}`) ? (
                          <input
                            type="number"
                            value={employee.customDeductions?.[deduction.id] || 0}
                            onChange={(e) => handleCustomDeductionChange(index, deduction.id, e.target.value)}
                            onBlur={handleCellBlur}
                            onKeyPress={handleKeyPress}
                            className="w-full px-1 py-1 border rounded text-right"
                            autoFocus
                          />
                        ) : (
                          <span className="text-red-600">
                            ₩{formatCurrency(employee.customDeductions?.[deduction.id] || 0)}
                          </span>
                        )}
                      </td>
                    ))}
                    <td className="border px-4 py-2 text-right text-red-600">
                      ₩{formatCurrency(calculated.totalDeduction)}
                    </td>
                    <td className="border px-4 py-2 text-right font-bold text-blue-600">
                      ₩{formatCurrency(calculated.netPay)}
                    </td>
                    <td className="border px-4 py-2">
                      {!confirmed && (
                        <button
                          onClick={() => handleConfirmSalary(employee)}
                          className="bg-purple-500 text-white px-2 py-1 rounded text-sm hover:bg-purple-600"
                        >
                          급여확정
                        </button>
                      )}
                      {confirmed && (
                        <div className="flex flex-col gap-1">
                          <span className="text-green-600 text-sm font-bold px-2 py-1">
                            ✓ 확정됨
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRevertSalary(employee)}
                            className="bg-white border border-red-400 text-red-500 px-2 py-1 rounded text-xs hover:bg-red-50"
                          >
                            확정 취소
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-gray-200 font-bold">
                <td colSpan="3" className="border px-4 py-2 text-right">합계:</td>
                <td className="border px-2 py-2 text-center">
                  {salaryData.reduce((sum, emp) => sum + (Number(emp.overtimeHours) || 0), 0)}h
                </td>
                <td className="border px-2 py-2 text-right text-blue-600">
                  ₩{formatCurrency(
                    salaryData.reduce((sum, emp) => sum + calculateSalary(emp).overtimePay, 0)
                  )}
                </td>
                <td className="border px-2 py-2 text-center">
                  {salaryData.reduce((sum, emp) => sum + (Number(emp.holidayWorkDays) || 0), 0)}일
                </td>
                <td className="border px-2 py-2 text-right text-blue-600">
                  ₩{formatCurrency(
                    salaryData.reduce((sum, emp) => sum + calculateSalary(emp).holidayPay, 0)
                  )}
                </td>
                <td className="border px-4 py-2 text-right">
                  ₩{formatCurrency(
                    salaryData.reduce((sum, emp) => sum + (Number(emp.mealAllowance) || 0), 0)
                  )}
                </td>
                <td className="border px-4 py-2 text-right">
                  ₩{formatCurrency(
                    salaryData.reduce((sum, emp) => sum + (Number(emp.transportAllowance) || 0), 0)
                  )}
                </td>
                <td className="border px-4 py-2 text-right">
                  ₩{formatCurrency(
                    salaryData.reduce((sum, emp) => sum + calculateSalary(emp).totalSalary, 0)
                  )}
                </td>
                {customDeductions.filter(d => d.active).map(deduction => (
                  <td key={deduction.id} className="border px-2 py-2 text-right text-red-600">
                    ₩{formatCurrency(
                      salaryData.reduce((sum, emp) =>
                        sum + (Number(emp.customDeductions?.[deduction.id]) || 0), 0
                      )
                    )}
                  </td>
                ))}
                <td className="border px-4 py-2 text-right text-red-600">
                  ₩{formatCurrency(
                    salaryData.reduce((sum, emp) => sum + calculateSalary(emp).totalDeduction, 0)
                  )}
                </td>
                <td className="border px-4 py-2 text-right text-blue-600">
                  ₩{formatCurrency(
                    salaryData.reduce((sum, emp) => sum + calculateSalary(emp).netPay, 0)
                  )}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}

export default RegularEmployeeBulkEntry;
